import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import { LayoutDashboard, Menu as MenuIcon, ShoppingBag, CreditCard } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { useCurrency } from "@/contexts/CurrencyContext";

const items = (t) => [
  { to: "/restaurant", icon: LayoutDashboard, label: t("dashboard"), end: true, tid: "rest-nav-dashboard" },
  { to: "/restaurant/menu", icon: MenuIcon, label: t("menu"), tid: "rest-nav-menu" },
  { to: "/restaurant/orders", icon: ShoppingBag, label: t("orders"), tid: "rest-nav-orders" },
  { to: "/restaurant/subscription", icon: CreditCard, label: t("subscription"), tid: "rest-nav-subscription" },
];

import { useLang } from "@/contexts/LanguageContext";

const STATUS_TINT = {
  pending: "bg-amber-500/10 text-amber-700",
  confirmed: "bg-blue-500/10 text-blue-700",
  preparing: "bg-indigo-500/10 text-indigo-700",
  ready: "bg-purple-500/10 text-purple-700",
  picked_up: "bg-orange-500/10 text-orange-700",
  out_for_delivery: "bg-orange-500/10 text-orange-700",
  delivered: "bg-emerald-500/10 text-emerald-700",
};

export default function RestaurantDashboard() {
  const { t } = useLang();
  const { formatPrice } = useCurrency();
  const [restaurant, setRestaurant] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      api.get("/my/restaurant"),
      api.get("/orders/mine"),
    ]);
    setRestaurant(r1.data); setOrders(r2.data);
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const stats = {
    total: orders.length,
    pending: orders.filter(o => ["pending", "confirmed", "preparing", "ready"].includes(o.status)).length,
    delivered: orders.filter(o => o.status === "delivered").length,
    revenue: orders.filter(o => o.status === "delivered").reduce((s, o) => s + o.total, 0),
  };

  const advance = async (id) => {
    await api.post(`/orders/${id}/advance`);
    load();
  };

  return (
    <DashboardLayout items={items(t)} title={t("dashboard")}>
      {restaurant && (
        <div className="so-card p-4 mb-6 flex items-center gap-4" data-testid="restaurant-card">
          <img src={restaurant.image_url} alt={restaurant.name} className="w-16 h-16 rounded-xl object-cover" />
          <div className="flex-1">
            <div className="font-bold text-lg">{restaurant.name}</div>
            <div className="text-sm text-[var(--so-text-soft)]">{restaurant.cuisine} · ⭐ {restaurant.rating}</div>
          </div>
          <span className="text-xs font-bold uppercase px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600">{restaurant.is_open ? "Open" : "Closed"}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 so-stagger">
        <StatCard label="Orders today" value={stats.total} tid="stat-total" />
        <StatCard label="Active" value={stats.pending} tint="text-amber-600" tid="stat-pending" />
        <StatCard label="Delivered" value={stats.delivered} tint="text-emerald-600" tid="stat-delivered" />
        <StatCard label="Revenue" value={formatPrice(stats.revenue)} tint="text-[var(--so-orange)]" tid="stat-revenue" />
      </div>

      <div className="so-card p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-bold text-lg">Live orders</h2>
          <Link to="/restaurant/orders" className="text-sm text-[var(--so-orange)] font-semibold">View all</Link>
        </div>
        {orders.filter(o => o.status !== "delivered").length === 0 ? (
          <div className="text-center py-8 text-[var(--so-text-soft)] text-sm">No active orders right now.</div>
        ) : (
          <div className="space-y-2">
            {orders.filter(o => o.status !== "delivered").slice(0, 6).map(o => (
              <div key={o.id} className="flex items-center gap-3 p-3 rounded-xl bg-[var(--so-bg)]" data-testid={`order-row-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{o.customer_name} · #{o.id.slice(0,6)}</div>
                  <div className="text-xs text-[var(--so-text-soft)]">{o.items.length} items · {formatPrice(o.total)}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${STATUS_TINT[o.status]}`}>{t(o.status)}</span>
                {["pending", "confirmed", "preparing"].includes(o.status) && (
                  <button onClick={() => advance(o.id)} className="so-btn-primary text-xs py-1.5 px-3" data-testid={`advance-${o.id}`}>
                    {o.status === "pending" ? "Confirm" : o.status === "confirmed" ? "Start" : "Mark Ready"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const StatCard = ({ label, value, tint, tid }) => (
  <div className="so-card p-4" data-testid={tid}>
    <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">{label}</div>
    <div className={`text-3xl font-extrabold mt-1 ${tint || ""}`}>{value}</div>
  </div>
);
